import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { JsonConvert } from 'json2typescript';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { LookupModel } from 'infrastructure-shared';
import { PerSocialSecuritySubtypeQueryModel } from '../model/per-social-security-subtype-query-model';
import { HrConfigurationService } from '../../../common/service/hr.configuration.service';
import { PerSocialSecuritySubtypeModel } from '../model/per-social-security-subtype-model';

@Injectable({
    providedIn: 'root',
})
export class PerSocialSecuritySubtypeService {
    endpoint: string;

    constructor(private httpClient: HttpClient, private configService: HrConfigurationService) {
        this.endpoint = configService.config.hr.personnel;
    }

    public queryPage(
        pagination: PerSocialSecuritySubtypeQueryModel,
    ): Observable<HvlPage<Array<PerSocialSecuritySubtypeModel>>> {
        const pattern = '/perSocialSecuritySubtype/socialSecuritySubtype/page';
        return this.httpClient
            .post<HvlResponse<HvlPage<PerSocialSecuritySubtypeModel[]>>>(this.endpoint + pattern, pagination)
            .pipe(
                map((hvlpage) => {
                    const converter = new JsonConvert();
                    if (hvlpage.body?.totalElements === 0) {
                        return new HvlPage<Array<PerSocialSecuritySubtypeModel>>(
                            [],
                            hvlpage.body.totalElements,
                            hvlpage.body.totalPages,
                            hvlpage.errorDetail,
                        );
                    }
                    const model = converter.deserializeArray(hvlpage.body.data, PerSocialSecuritySubtypeModel);
                    return new HvlPage<Array<PerSocialSecuritySubtypeModel>>(
                        model,
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }),
            );
    }

    public lookup(): Observable<Array<LookupModel>> {
        const pattern = '/perSocialSecuritySubtype/socialSecuritySubtype/lookup';
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.get<HvlResponse<Array<LookupModel>>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], LookupModel);
            }),
        );
    }

    public queryListByLocale(
        perSocialSecuritySubtypeQueryModel: PerSocialSecuritySubtypeQueryModel,
        locale: string,
    ): Observable<Array<PerSocialSecuritySubtypeModel>> {
        const pattern = '/perSocialSecuritySubtype/socialSecuritySubtype/query/byLocale/' + locale;
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<PerSocialSecuritySubtypeModel>>>(
                this.endpoint + pattern,
                converter.serialize(perSocialSecuritySubtypeQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], PerSocialSecuritySubtypeModel);
                }),
            );
    }

    public getById(id: number): Observable<PerSocialSecuritySubtypeModel> {
        const pattern = '/perSocialSecuritySubtype/' + id;
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.get<HvlResponse<PerSocialSecuritySubtypeModel>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerSocialSecuritySubtypeModel);
            }),
        );
    }

    public update(
        perSocialSecuritySubtypeModel: PerSocialSecuritySubtypeModel,
    ): Observable<PerSocialSecuritySubtypeModel> {
        const pattern = '/perSocialSecuritySubtype/socialSecuritySubtype';
        const converter = new JsonConvert();
        return this.httpClient
            .put<HvlResponse<PerSocialSecuritySubtypeModel>>(
                this.endpoint + pattern,
                converter.serialize(perSocialSecuritySubtypeModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerSocialSecuritySubtypeModel);
                }),
            );
    }

    public getList(): Observable<Array<PerSocialSecuritySubtypeModel>> {
        const pattern = '/perSocialSecuritySubtype/socialSecuritySubtype';
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.get<HvlResponse<Array<PerSocialSecuritySubtypeModel>>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], PerSocialSecuritySubtypeModel);
            }),
        );
    }

    public queryList(
        perSocialSecuritySubtypeQueryModel: PerSocialSecuritySubtypeQueryModel,
    ): Observable<Array<PerSocialSecuritySubtypeModel>> {
        const pattern = '/perSocialSecuritySubtype/socialSecuritySubtype/query';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<PerSocialSecuritySubtypeModel>>>(
                this.endpoint + pattern,
                converter.serialize(perSocialSecuritySubtypeQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], PerSocialSecuritySubtypeModel);
                }),
            );
    }

    public save(
        perSocialSecuritySubtypeModel: PerSocialSecuritySubtypeModel,
    ): Observable<PerSocialSecuritySubtypeModel> {
        const pattern = '/perSocialSecuritySubtype/socialSecuritySubtype';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<PerSocialSecuritySubtypeModel>>(
                this.endpoint + pattern,
                converter.serialize(perSocialSecuritySubtypeModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerSocialSecuritySubtypeModel);
                }),
            );
    }

    public deleteById(id: number): Observable<any> {
        const pattern = '/perSocialSecuritySubtype/socialSecuritySubtype/' + id;
        return this.httpClient.delete(this.endpoint + pattern);
    }

    public lookupQuery(queryModel: PerSocialSecuritySubtypeQueryModel): Observable<Array<LookupModel>> {
        const pattern = '/perSocialSecuritySubtype/socialSecuritySubtype/lookup/query';
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.post<HvlResponse<Array<LookupModel>>>(serviceUrl, queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], LookupModel);
            }),
        );
    }
}
